/* global Office */

import { getCurrentSelection, type CurrentSelection } from "./powerpointRunner";

/**
 * 編集ビュー上での選択変更を購読するためのウォッチャー。
 *
 * - 内部で `Office.EventType.DocumentSelectionChanged` を 1 回だけ登録する
 * - 登録は最初の subscribe 呼び出し時、解除は最後の unsubscribe 時 (lazy)
 * - イベント発火のたびに `getCurrentSelection()` を呼び、全リスナーへ伝搬する
 *
 * 注意: PowerPoint 編集ビューでスライドや図形を選択した際にも
 * `DocumentSelectionChanged` は発火するが、PowerPoint Online やバージョン差で
 * 挙動が変わる可能性がある。テストは契約レベルに留め、実機で網羅する。
 */

type Listener = (selection: CurrentSelection) => void;

const listeners = new Set<Listener>();
let officeHandler: ((arg: unknown) => void) | null = null;

export function subscribeSelection(listener: Listener): () => void {
    listeners.add(listener);
    if (listeners.size === 1) {
        attachOfficeHandler();
    }
    return () => {
        listeners.delete(listener);
        if (listeners.size === 0) {
            detachOfficeHandler();
        }
    };
}

async function dispatch(): Promise<void> {
    const sel = await getCurrentSelection();
    listeners.forEach((l) => l(sel));
}

function attachOfficeHandler() {
    officeHandler = () => {
        void dispatch();
    };
    Office.context.document.addHandlerAsync(
        Office.EventType.DocumentSelectionChanged,
        officeHandler
    );
}

function detachOfficeHandler() {
    if (!officeHandler) return;
    Office.context.document.removeHandlerAsync(Office.EventType.DocumentSelectionChanged, {
        handler: officeHandler,
    });
    officeHandler = null;
}

/**
 * テスト用: 内部状態を初期化する。
 */
export function __resetForTest(): void {
    listeners.clear();
    officeHandler = null;
}
